const STORAGE_KEY = 'hitpoly_virtual_assistant_memory';
const GUEST_ID_KEY = 'hitpoly_assistant_guest_id';
const STORAGE_VERSION = 6;
const API_URL = 'https://apiweb.hitpoly.com/ajax/ChatAssistantController.php';

const getGuestId = () => {
  let gid = localStorage.getItem(GUEST_ID_KEY);
  if (!gid) {
    gid = 'guest_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem(GUEST_ID_KEY, gid);
  }
  return gid;
};

export const createEmptyAssistantMemory = () => ({
  version: STORAGE_VERSION,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  answers: {},
  messages: [],
  persona: null,
  diagnosis: null,
  sessionId: null, // Guardamos el ID de la sesión en base de datos
});

/**
 * Carga la memoria desde la API o fallback a localStorage.
 */
export const loadAssistantMemory = async (userId = null) => {
  if (typeof window === 'undefined') return createEmptyAssistantMemory();

  const guestId = getGuestId();

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        funcion: 'obtener_o_crear_sesion',
        user_id: userId,
        guest_id: guestId
      }),
    });

    const rawText = await response.text();

    let result;
    try {
      result = JSON.parse(rawText);
    } catch (parseError) {
      return createEmptyAssistantMemory();
    }

    if (response.ok && result.status === 'success' && result.data) {
      const db = result.data;
      return {
        version: STORAGE_VERSION,
        createdAt: db.created_at,
        updatedAt: db.updated_at,
        answers: db.answers || {},
        messages: db.messages || [],
        persona: db.persona_data || null,
        diagnosis: db.diagnosis || null,
        sessionId: db.id,
      };
    }
  } catch (error) {
    // Network error fallback
  }

  // FALLBACK A LOCALSTORAGE (LOGICA ANTIGUA)
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return createEmptyAssistantMemory();

    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORAGE_VERSION) {
      return createEmptyAssistantMemory();
    }
    return parsed;
  } catch (error) {
    return createEmptyAssistantMemory();
  }
};

/**
 * Guarda la memoria de forma asíncrona en la base de datos.
 */
export const saveAssistantMemory = async (memory, userId = null) => {
  if (typeof window === 'undefined') return memory;

  const nextMemory = {
    ...memory,
    version: STORAGE_VERSION,
    updatedAt: new Date().toISOString(),
  };

  // 1. Guardar en LocalStorage para redundancia inmediata
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextMemory));

  // 2. Sincronizar con API si tenemos sessionId
  if (nextMemory.sessionId) {
    try {
      // Sincronizar respuestas
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funcion: 'actualizar_respuestas',
          session_id: nextMemory.sessionId,
          answers: nextMemory.answers
        }),
      });

      // Si hay diagnóstico nuevo, guardarlo
      if (nextMemory.diagnosis) {
        await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            funcion: 'finalizar_diagnostico',
            session_id: nextMemory.sessionId,
            diagnosis: nextMemory.diagnosis
          }),
        });
      }
    } catch (err) {
      // Sync failed silently
    }
  }

  return nextMemory;
};

/**
 * Guarda un mensaje individual de forma asíncrona.
 */
export const saveChatMessage = async (sessionId, role, text) => {
  if (!sessionId) return;
  try {
    await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        funcion: 'guardar_mensaje',
        session_id: sessionId,
        role: role,
        text: text
      }),
    });
  } catch (e) {
    // silently ignore
  }
};

export const getChatHistory = async (userId = null) => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        funcion: 'obtener_historial',
        user_id: userId,
        guest_id: getGuestId()
      }),
    });
    const result = await response.json();
    return result.status === 'success' ? result.data : [];
  } catch (e) {
    return [];
  }
};

export const clearAssistantMemory = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
};

const assistantMemory = {
  createEmptyAssistantMemory,
  loadAssistantMemory,
  saveAssistantMemory,
  saveChatMessage,
  clearAssistantMemory,
  getGuestId,
  getChatHistory
};

export default assistantMemory;
